import { PropsWithChildren, useCallback, useEffect, useState } from "react";
import { OverlayEvent } from "@features/onsiteEshop/utils/types";
import { CustomEventEnum } from "@typings/CustomEvent";
import { cn } from "@util/helpers";

type EshopOverlayVisibilityGateProps = {
  /** A unique identifier to determine what is the children component is. */
  componentId: string;
} & PropsWithChildren;
/**
 * Display BT components when eshop send overlay:deprioritize event or default to true if no event is sent.
 * Doc: https://sph.atlassian.net/wiki/spaces/CHB/pages/2212954211/Onsite+Checkout+Integration+Guide#4.2.-Handling-of-overlay:prioritize-and-overlay:deprioritize
 */
const EshopOverlayVisibilityGate = ({
  componentId,
  children,
}: EshopOverlayVisibilityGateProps): React.ReactElement => {
  const [isBTComponentsVisible, setBTComponentsVisible] = useState(true);

  const handleOverlayPrioritize = useCallback(
    ({ detail }: OverlayEvent) => {
      if (typeof window === "undefined") return;
      if (!detail.id) return;

      if (detail.id === "eshop-onsite-checkout") {
        setBTComponentsVisible(false);
      }

      if (componentId === "catfish") {
        document.body.classList.toggle("hide-mobile-ads");
      }
    },
    [componentId]
  );

  const handleOverlayDeprioritize = useCallback(
    ({ detail }: OverlayEvent) => {
      if (typeof window === "undefined") return;
      if (!detail.id) return;

      if (detail.id === "eshop-onsite-checkout") {
        setBTComponentsVisible(true);
      }

      if (componentId === "catfish") {
        document.body.classList.remove("hide-mobile-ads");
      }
    },
    [componentId]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const listeners = window.addEventListener;

    listeners(CustomEventEnum.OverlayPrioritize, handleOverlayPrioritize);
    listeners(CustomEventEnum.OverlayDeprioritize, handleOverlayDeprioritize);

    return () => {
      const removeListeners = window.removeEventListener;

      removeListeners(
        CustomEventEnum.OverlayPrioritize,
        handleOverlayPrioritize
      );

      removeListeners(
        CustomEventEnum.OverlayDeprioritize,
        handleOverlayDeprioritize
      );
    };
  }, [handleOverlayPrioritize, handleOverlayDeprioritize]);

  return (
    <div
      data-testid="eshop-overlay-visibility-gate"
      className={cn({ "!hidden": !isBTComponentsVisible })}
    >
      {children}
    </div>
  );
};

export default EshopOverlayVisibilityGate;
